import React, { useEffect, useMemo } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import CancelIcon from '@mui/icons-material/Cancel';
import SaveIcon from '@mui/icons-material/Save';
import {
  Box,
  Button,
  Dialog,
  Grid,
  IconButton,
  Slide,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type { TransitionProps } from '@mui/material/transitions';
import { CalendarClock } from 'lucide-react';
import { useForm } from 'react-hook-form';
import CustomTextField from '../../common/CustomTextField';
import CustomDropdownField from '../../common/CustomDropdownFiled';
import { useModalStyles } from '../../common/useModalStyles';
import { Job, SCHEDULE_TYPE_OPTIONS } from '../../../redux/slice/jobSlice';
import { useAuthSlice } from '../../../redux/slice/authSlice';
import { setSingleAlertObj } from '../../../redux/slice/commonSlice';
import { useAppDispatch } from '../../../redux/slice/redux-hooks';
import { FieldLabel } from '../../../utils/Utility';

interface JobDetailsPopupProps {
  open: boolean;
  job: Job;
  onClose: () => void;
  onSave: (jobData: any) => void; // Add callback for save
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>
) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const JobDetailsPopup: React.FC<JobDetailsPopupProps> = ({
  open,
  job,
  onClose,
  onSave,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    isDark,
    tokens,
    paperSx,
    bodySx,
    footerSx,
    dropdownWrapWithLabelSx,
    inputFieldWithLabelSx,
    primaryBtnSx,
    cancelBtnSx,
  } = useModalStyles();

  const heroBg = isDark ? 'transparent' : '#FAFBFD';
  const heroBdr = isDark ? 'rgba(255,255,255,0.05)' : '#F1F5F9';
  const subtitleColor = isDark ? '#94a3b8' : '#64748b';

  const scheduleTypeDisplayOptions = useMemo(
    () =>
      SCHEDULE_TYPE_OPTIONS.map((o) => ({
        ...o,
        label: o.label.charAt(0) + o.label.slice(1).toLowerCase(),
      })),
    []
  );

  const dispatch = useAppDispatch();
  const { userInfo } = useAuthSlice();
  const { control, setValue, handleSubmit, reset, watch } = useForm<any>({
    defaultValues: {
      jobName: '',
      scheduled: '',
      className: '',
      inActiveDate: '',
      lastSyncDateTime: '',
      lastRunTime: '',
      dayOfMonth: '',
      dayOfWeek: '',
      scheduleTime: '',
      scheduleType: '',
      description: '',//CS-1571
    }
  });

  // Watch for schedule type changes
  const watchedScheduleType = watch('scheduleType');
  //CS-1571
  const watchedDescription = watch('description');

  useEffect(() => {
    if (job && open) {
      reset({
        jobName: job.jobName || '',
        scheduled: job.scheduled ? 'YES' : 'NO',
        className: job.className || '',
        inActiveDate: job.inActiveDate || '',
        lastSyncDateTime: job.lastSyncDateTime || '',
        lastRunTime: job.lastRunTime || '',
        dayOfMonth: job.dayOfMonth?.toString() || '',
        dayOfWeek: job.dayOfWeek || '',
        scheduleTime: job.scheduleTime || '',
        scheduleType: job.scheduleType ? job.scheduleType.toLowerCase() : '',
        jobParams: job.jobParams || '',
        description: job.description || '',
      });
    }
  }, [job, reset, open]);

  // Clear dayOfWeek and dayOfMonth when schedule type changes
  useEffect(() => {
    if (watchedScheduleType) {
      const scheduleType = watchedScheduleType.toLowerCase();
      
      // Clear dayOfWeek if not weekly
      if (scheduleType !== 'weekly') {
        setValue('dayOfWeek', '');
      }
      
      // Clear dayOfMonth if not monthly
      if (scheduleType !== 'monthly') {
        setValue('dayOfMonth', '');
      }
    }
  }, [watchedScheduleType, setValue]);

  // Validation function for schedule time based on schedule type
  const validateScheduleTime = (scheduleTime: string, scheduleType: string): { isValid: boolean; message: string } => {
    const scheduleTypeLower = scheduleType.toLowerCase();
    
    switch (scheduleTypeLower) {
      case 'minutely':
        // For minutely: expects minutes (0-59)
        const minutes = parseInt(scheduleTime);
        if (isNaN(minutes)) {
          return { isValid: false, message: 'For minutely schedule, time must be a valid number representing minutes.' };
        }
        if (minutes < 0 || minutes > 59) {
          return { isValid: false, message: 'For minutely schedule, time must be between 0 and 59 minutes.' };
        }
        return { isValid: true, message: '' };

      case 'hourly':
        // For hourly: expects hours (1-23, where >1 means every N hours, 1 means every hour)
        const hours = parseInt(scheduleTime);
        if (isNaN(hours)) {
          return { isValid: false, message: 'For hourly schedule, time must be a valid number representing hours.' };
        }
        if (hours < 1) {
          return { isValid: false, message: 'For hourly schedule, time must be at least 1 hour.' };
        }
        return { isValid: true, message: '' };

      case 'daily':
      case 'weekly':
      case 'monthly':
        // For daily/weekly/monthly: expects time in HH:mm format (ISO_LOCAL_TIME)
        const timeRegex = /^([0-1][0-9]|2[0-3]):[0-5][0-9]$/;
        if (!timeRegex.test(scheduleTime)) {
          return { isValid: false, message: `For ${scheduleTypeLower} schedule, time must be in HH:mm format (e.g., 14:30, 09:15).` };
        }
        
        const [hourStr, minuteStr] = scheduleTime.split(':');
        const hour = parseInt(hourStr);
        const minute = parseInt(minuteStr);
        
        if (hour < 0 || hour > 23) {
          return { isValid: false, message: 'Hour must be between 00 and 23.' };
        }
        if (minute < 0 || minute > 59) {
          return { isValid: false, message: 'Minute must be between 00 and 59.' };
        }
        return { isValid: true, message: '' };

      case 'cron':
        // For cron: accepts any string (cron expression)
        if (!scheduleTime || scheduleTime.trim() === '') {
          return { isValid: false, message: 'For cron schedule, a valid cron expression is required.' };
        }
        return { isValid: true, message: '' };

      default:
        return { isValid: false, message: 'Invalid schedule type selected.' };
    }
  };

  // Helper function to get schedule time placeholder and helper text
  const getScheduleTimeInfo = (scheduleType: string) => {
    const scheduleTypeLower = scheduleType?.toLowerCase();
    
    switch (scheduleTypeLower) {
      case 'minutely':
        return {
          placeholder: 'Enter minutes (0-59)',
          helperText: 'Enter number of minutes (e.g., 30 for every 30 minutes)'
        };
      case 'hourly':
        return {
          placeholder: 'Enter hours (1+)',
          helperText: 'Enter number of hours (e.g., 2 for every 2 hours, 1 for every hour)'
        };
      case 'daily':
        return {
          placeholder: 'HH:mm (e.g., 14:30)',
          helperText: 'Enter time in 24-hour format (e.g., 14:30 for 2:30 PM)'
        };
      case 'weekly':
        return {
          placeholder: 'HH:mm (e.g., 09:00)',
          helperText: 'Enter time in 24-hour format for the selected day'
        };
      case 'monthly':
        return {
          placeholder: 'HH:mm (e.g., 12:00)',
          helperText: 'Enter time in 24-hour format for the selected day of month'
        };
      case 'cron':
        return {
          placeholder: 'Enter cron expression',
          helperText: 'Enter a valid cron expression (e.g., 0 0 12 * * ?)'
        };
      default:
        return {
          placeholder: 'Enter schedule time',
          helperText: 'Select a schedule type first'
        };
    }
  };

  const normalizeKey = (key: string) =>
    key.replace(/\s+/g, "_").replace(/[()]/g, "").toLowerCase();

  const keyMap = Object.keys(job.jobParams ?? "").reduce((acc, key) => {
    acc[normalizeKey(key)] = key;
    return acc;
  }, {} as Record<string, string>);

  const handleClose = () => {
    setTimeout(() => {
      reset();
      onClose();
    }, 300);
  };

  const onSubmit = (data: any) => {
    if (!job.jobId) {
      dispatch(setSingleAlertObj({
        message: 'Invalid job ID. Cannot save job details.',
        alertType: 'error'
      }));
      return;
    }

    if (!data.scheduleType || data.scheduleType.trim() === '') {
      dispatch(setSingleAlertObj({
        message: 'Schedule type is required. Please select a valid schedule type.',
        alertType: 'error'
      }));
      return;
    }

    if (!data.scheduleTime || data.scheduleTime.trim() === '') {
      dispatch(setSingleAlertObj({
        message: 'Schedule time is required. Please enter a valid schedule time.',
        alertType: 'error'
      }));
      return;
    }

    // Validate schedule time based on schedule type
    const scheduleTimeValidation = validateScheduleTime(data.scheduleTime, data.scheduleType);
    if (!scheduleTimeValidation.isValid) {
      dispatch(setSingleAlertObj({
        message: scheduleTimeValidation.message,
        alertType: 'error'
      }));
      return;
    }

    // Validate Day of Week for weekly schedule
    if (data.scheduleType.toLowerCase() === 'weekly' && (!data.dayOfWeek || data.dayOfWeek.trim() === '')) {
      dispatch(setSingleAlertObj({
        message: 'Day of Week is required for weekly schedule.',
        alertType: 'error'
      }));
      return;
    }

    // Validate Day of Month for monthly schedule
    if (data.scheduleType.toLowerCase() === 'monthly') {
      if (!data.dayOfMonth || data.dayOfMonth.trim() === '') {
        dispatch(setSingleAlertObj({
          message: 'Day of Month is required for monthly schedule.',
          alertType: 'error'
        }));
        return;
      }
      
      const dayOfMonth = parseInt(data.dayOfMonth);
      if (isNaN(dayOfMonth) || dayOfMonth < 1 || dayOfMonth > 31) {
        dispatch(setSingleAlertObj({
          message: 'Day of Month must be between 1 and 31.',
          alertType: 'error'
        }));
        return;
      }
    }

    const scheduleTypeLower = data.scheduleType.toLowerCase();

    const rebuiltParams: Record<string, string> = {};

    Object.entries(data.jobParams as Record<string, string>).forEach(
      ([key, value]) => {
        const originalKey = keyMap[key];
        if (originalKey) {
          rebuiltParams[originalKey] = value;
        }
      }
    );
    
    const editJobRequest = {
      jobId: job.jobId.toString(),
      scheduleType: data.scheduleType,
      scheduleTime: data.scheduleTime,
      // Only include dayOfWeek if schedule type is weekly
      dayOfWeek: scheduleTypeLower === 'weekly' ? data.dayOfWeek : undefined,
      // Only include dayOfMonth if schedule type is monthly
      dayOfMonth: scheduleTypeLower === 'monthly' && data.dayOfMonth ? parseInt(data.dayOfMonth) : undefined,
      description: data.description || null,//CS-1571
      auditUserId: userInfo?.userId?.toString(),
      jobParams: rebuiltParams
    };
    
    onSave(editJobRequest);
    handleClose();
  };

  const dayOfMonthOptions = Array.from({ length: 31 }, (_, i) => ({
    value: (i + 1).toString(),
    label: (i + 1).toString(),
  })).sort((a, b) => Number(a.value) - Number(b.value));


  const dayOfWeekOptions = [
    { value: 'MONDAY', label: 'Monday' },
    { value: 'TUESDAY', label: 'Tuesday' },
    { value: 'WEDNESDAY', label: 'Wednesday' },
    { value: 'THURSDAY', label: 'Thursday' },
    { value: 'FRIDAY', label: 'Friday' },
    { value: 'SATURDAY', label: 'Saturday' },
    { value: 'SUNDAY', label: 'Sunday' },
  ];

  useEffect(() => {
    if (!job?.jobParams) return;
    Object.entries(job.jobParams).forEach(([key, value]) => {
      const fieldName = normalizeKey(key);
      setValue(`jobParams.${fieldName}`, value);
    });
  }, [job, setValue]);


  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      scroll="paper"
      TransitionComponent={Transition}
      keepMounted
      PaperProps={{
        elevation: 0,
        sx: paperSx(isMobile),
      }}
      BackdropProps={{
        sx: {
          backgroundColor: isDark ? 'rgba(0,0,0,0.78)' : 'rgba(15,23,42,0.5)',
          backdropFilter: 'blur(6px)',
        },
      }}
      sx={{
        '& .MuiDialog-paper': {
          maxWidth: { xs: '95vw', md: '900px' },
          width: { xs: '100vw', md: 'auto' },
          m: { xs: 2, md: 2 },
        }
      }}
    >
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,      
          overflow: "hidden" 
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            px: 3,
            py: 2.25,
            background: heroBg,
            borderBottom: `1px solid ${heroBdr}`,
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, minWidth: 0, flex: 1 }}>
            <Box
              sx={{
                width: 46,
                height: 46,
                borderRadius: '13px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                background: `${tokens.accent}18`,
                border: `1px solid ${tokens.accent}30`,
                color: tokens.accent,
              }}
            >
              <CalendarClock size={22} color={tokens.accent} strokeWidth={1.8} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: '1rem',
                  fontWeight: 700,
                  letterSpacing: '-0.01em',
                  color: tokens.textPrimary,
                  lineHeight: 1.25,
                }}
              >
                Agent Details
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: subtitleColor,
                  mt: 0.35,
                  lineHeight: 1.45,
                }}
              >
                View read-only fields and edit schedule, parameters, and description
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{
              flexShrink: 0,
              width: 34,
              height: 34,
              borderRadius: '9px',
              color: tokens.textSecondary,
              bgcolor: isDark ? 'rgba(255,255,255,0.05)' : '#F3F4F6',
              transition: 'all 0.2s',
              '&:hover': {
                bgcolor: isDark ? 'rgba(255,255,255,0.10)' : '#E5E7EB',
                transform: 'rotate(90deg)',
              },
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>

        <Box
          sx={{
            ...bodySx,
            minHeight: 0,
            '& .MuiFormHelperText-root': {
              fontSize: '0.8125rem',
              lineHeight: 1.45,
              mt: 0.75,
              color: subtitleColor,
            },
            '& .MuiInputLabel-root': {
              fontSize: '0.875rem',
            },
            '& .MuiInputBase-input': {
              fontSize: '0.9rem',
            },
            '&::-webkit-scrollbar': { width: 6, height: 6 },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': {
              background: isDark ? 'rgba(255,255,255,0.12)' : '#CBD5E1',
              borderRadius: 4,
            },
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box>
                <FieldLabel label="Agent Name" tokens={tokens} />
              <Box sx={inputFieldWithLabelSx()}>
                <CustomTextField
                  fullWidth
                  control={control}
                  controllerName="jobName"
                  label=""
                  isDisabled={true}
                />
              </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box>
                <FieldLabel label="Last Run Time" tokens={tokens} />
              <Box sx={inputFieldWithLabelSx()}>
                <CustomTextField
                  fullWidth
                  control={control}
                  controllerName="lastRunTime"
                  label=""
                  isDisabled={true}
                />
              </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box>
                <FieldLabel label="Scheduled" tokens={tokens} />
              <Box sx={inputFieldWithLabelSx()}>
                <CustomTextField
                  fullWidth
                  control={control}
                  controllerName="scheduled"
                  label=""
                  isDisabled={true}
                />
              </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box>
                <FieldLabel label="Class Name" tokens={tokens} />
              <Box sx={inputFieldWithLabelSx()}>
                <CustomTextField
                  fullWidth
                  control={control}
                  controllerName="className"
                  label=""
                  isDisabled={true}
                />
              </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box>
                <FieldLabel label="InActiveDate" tokens={tokens} />
              <Box sx={inputFieldWithLabelSx()}>
                <CustomTextField
                  fullWidth
                  control={control}
                  controllerName="inActiveDate"
                  label=""
                  isDisabled={true}
                />
              </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box>
                <FieldLabel label="Last Sync Date Time" tokens={tokens} />
              <Box sx={inputFieldWithLabelSx()}>
                <CustomTextField
                  fullWidth
                  control={control}
                  controllerName="lastSyncDateTime"
                  label=""
                  isDisabled={true}
                />
              </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box>
                <FieldLabel label="Schedule Time" required tokens={tokens} />
              <Box sx={inputFieldWithLabelSx()}>
                <CustomTextField
                  fullWidth
                  control={control}
                  controllerName="scheduleTime"
                  label=""
                  placeholder={getScheduleTimeInfo(watchedScheduleType).placeholder}
                  helperText={getScheduleTimeInfo(watchedScheduleType).helperText}
                  rules={{ required: 'Schedule Time is required' }}
                />
              </Box>
              </Box>
            </Grid>

            {job.jobParams &&
              Object.entries(job.jobParams).map(([key, value]) => {
                const fieldName = normalizeKey(key);
                return (
                  <Grid item xs={12} md={6} key={`job-param-${fieldName}`}>
                    <Box>
                      <FieldLabel label={key} required tokens={tokens} />
                    <Box sx={inputFieldWithLabelSx()}>
                      <CustomTextField
                        fullWidth
                        control={control}
                        controllerName={`jobParams.${fieldName}`}
                        label=""
                        defaultValue={value}
                        rules={{
                          required: `${key} is required`,
                        }}
                      />
                    </Box>
                    </Box>
                  </Grid>
                );
              })}

            <Grid item xs={12} md={6}>
              <Box>
                <FieldLabel label="Schedule Type" required tokens={tokens} />
              <Box sx={dropdownWrapWithLabelSx()}>
                <CustomDropdownField
                  options={scheduleTypeDisplayOptions}
                  control={control}
                  name="scheduleType"
                  label=""
                  rules={{ required: 'Schedule Type is required' }}
                />
              </Box>
              </Box>
            </Grid>

            {watchedScheduleType?.toLowerCase() === 'weekly' && (
              <Grid item xs={12} md={6}>
                <Box>
                  <FieldLabel label="Day Of Week" required tokens={tokens} />
                <Box sx={dropdownWrapWithLabelSx()}>
                  <CustomDropdownField
                    options={dayOfWeekOptions}
                    control={control}
                    name="dayOfWeek"
                    label=""
                    rules={{
                      required: 'Day of Week is required for weekly schedule',
                    }}
                    sortOptions={false}
                  />
                </Box>
                </Box>
              </Grid>
            )}

            {watchedScheduleType?.toLowerCase() === 'monthly' && (
              <Grid item xs={12} md={6}>
                <Box>
                  <FieldLabel label="Day Of Month" required tokens={tokens} />
                <Box sx={dropdownWrapWithLabelSx()}>
                  <CustomDropdownField
                    options={dayOfMonthOptions}
                    control={control}
                    name="dayOfMonth"
                    label=""
                    sortOptions={false}
                    rules={{
                      required: 'Day of Month is required for monthly schedule',
                    }}
                  />
                </Box>
                </Box>
              </Grid>
            )}

            {/* CS-1571 */}
            <Grid item xs={12}>
              <Box>
                <FieldLabel label="Description" tokens={tokens} />
              <Box sx={inputFieldWithLabelSx()}>
                <CustomTextField
                  fullWidth
                  control={control}
                  controllerName="description"
                  label=""
                  multiline
                  rows={4}
                  placeholder="Enter job description"
                  helperText={`${(watchedDescription || '').length}/1000`}
                  maxLength={1000}
                  rules={{
                    maxLength: {
                      value: 1000,
                      message: 'Description cannot exceed 1000 characters',
                    },
                  }}
                />
              </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ ...footerSx, flexShrink: 0 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 1.5,
            }}
          >
            <Button
              fullWidth
              variant="contained"
              type="submit"
              startIcon={<SaveIcon sx={{ fontSize: '1rem !important' }} />}
              sx={primaryBtnSx}
            >
              Save
            </Button>
            <Button
              fullWidth
              variant="outlined"
              type="button"
              onClick={handleClose}
              startIcon={<CancelIcon sx={{ fontSize: '1rem !important' }} />}
              sx={cancelBtnSx}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default JobDetailsPopup;
